import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle2, ArrowRight, RotateCcw, Brain, Shield,
  ChevronLeft, Loader2, FileText, Users, Send, Edit3, RefreshCw,
  Sparkles, Heart, Lock, Unlock, Eye, ClipboardCheck, Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function DomainLabel({ domain }) {
  const labels = {
    self_regulation: 'Self-Regulation',
    social_environment: 'Social Environment',
    substance_coping: 'Substance & Coping',
    daily_structure: 'Daily Structure',
    authority_thinking: 'Authority & Thinking',
    future_orientation: 'Future & Purpose',
    conflict_decisions: 'Conflict & Decisions',
    stability_change: 'Stability & Change',
  };
  return <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{labels[domain] || domain}</span>;
}

function RiskLevelBadge({ level, score }) {
  const colors = {
    low: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    moderate: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    elevated: 'bg-orange-100 text-orange-700 border-orange-300',
    high: 'bg-red-100 text-red-700 border-red-300',
    critical: 'bg-red-200 text-red-800 border-red-400',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors[level] || colors.low}`}>
      {score !== undefined ? `${score}/10` : ''} {level?.toUpperCase()}
    </span>
  );
}

function QuestionView({ question, onAnswer, index, total }) {
  const [selected, setSelected] = useState(null);
  return (
    <div className="space-y-6" data-testid="core8-question-view">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">Question {index + 1} of {total}</span>
        <DomainLabel domain={question.domain} />
      </div>
      <h2 className="text-xl font-semibold text-foreground leading-relaxed" style={{ fontFamily: 'Manrope' }} data-testid="question-text">
        {question.text}
      </h2>
      <div className="space-y-3 mt-6">
        {question.options.map((opt, i) => {
          const hoverColors = [
            'hover:bg-emerald-50 hover:border-emerald-300',
            'hover:bg-yellow-50 hover:border-yellow-300',
            'hover:bg-orange-50 hover:border-orange-300',
            'hover:bg-red-50 hover:border-red-300',
          ];
          return (
            <button
              key={opt.key}
              data-testid={`option-${opt.key}`}
              onClick={() => setSelected(opt.key)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                selected === opt.key
                  ? 'bg-primary/10 border-primary text-foreground font-medium'
                  : `bg-card border-border text-foreground ${hoverColors[i]}`
              }`}
            >
              <span className="font-bold mr-3 text-muted-foreground">{opt.key}.</span>
              {opt.text}
            </button>
          );
        })}
      </div>
      <Button
        data-testid="next-question-btn"
        onClick={() => { if (selected) onAnswer(question.id, selected); setSelected(null); }}
        disabled={!selected}
        className="w-full mt-4 py-3 rounded-xl"
        size="lg"
      >
        {index + 1 < total ? 'Next Question' : 'Complete Assessment'}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function StaffResultsView({ results, onInterpret, interpreting, interpretation, participantName }) {
  const [showAnswers, setShowAnswers] = useState(false);
  const riskScores = results?.risk_scores || {};
  const domainScores = results?.domain_scores || {};
  const stage = results?.stage_of_change || {};
  const overall = results?.overall_risk || {};
  const responses = results?.responses || {};
  const questionOrder = results?.question_order || [];

  return (
    <div className="space-y-6" data-testid="core8-results-view">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <Shield className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>CORE-8 Profile Complete</h2>
        {participantName && <p className="text-muted-foreground mt-1">Assessment for {participantName}</p>}
      </div>

      <Card className="border-0 shadow-sm rounded-2xl">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Overall Risk Profile</h3>
            <RiskLevelBadge level={overall.level} score={overall.highest_score} />
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-muted rounded-xl p-3 text-center">
              <span className="text-muted-foreground text-xs">Composite</span>
              <p className="text-xl font-bold text-foreground">{overall.composite_score}/10</p>
            </div>
            <div className="bg-muted rounded-xl p-3 text-center">
              <span className="text-muted-foreground text-xs">Highest Domain</span>
              <p className="text-xl font-bold text-foreground">{overall.highest_score}/10</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Risk Categories (1-10)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(riskScores).map(([key, val]) => (
            <div key={key} data-testid={`risk-category-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-foreground">{val.label}</span>
                <RiskLevelBadge level={val.level} score={val.score} />
              </div>
              <div className="w-full bg-muted rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${
                    val.score <= 3 ? 'bg-emerald-500' : val.score <= 5 ? 'bg-yellow-500' : val.score <= 7 ? 'bg-orange-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${(val.score / 10) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{val.contributing_domains?.join(' + ')}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Domain Breakdown (8 Domains)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(domainScores).map(([key, val]) => (
              <div key={key} className="bg-muted rounded-xl p-3" data-testid={`domain-${key}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-muted-foreground">{val.label}</span>
                  <span className="text-xs font-mono text-foreground">{val.raw_score}/{val.max_score}</span>
                </div>
                <div className="w-full bg-background rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${val.percentage <= 30 ? 'bg-emerald-500' : val.percentage <= 50 ? 'bg-yellow-500' : val.percentage <= 70 ? 'bg-orange-500' : 'bg-red-500'}`}
                    style={{ width: `${val.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm rounded-2xl">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <RotateCcw className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Stage of Change</h3>
              <p className="text-primary font-medium">{stage.label}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{stage.description}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {!interpretation && (
        <Button
          data-testid="generate-interpretation-btn"
          onClick={onInterpret}
          disabled={interpreting}
          className="w-full py-3 rounded-xl"
          size="lg"
        >
          {interpreting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Brain className="mr-2 h-4 w-4" />}
          {interpreting ? 'Analyzing with AI...' : 'Generate AI Interpretation & Plan'}
        </Button>
      )}

      {/* Full Assessment Answers */}
      {Object.keys(responses).length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="cursor-pointer" onClick={() => setShowAnswers(!showAnswers)}>
            <CardTitle className="text-base flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
              <span className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Full Assessment Answers ({Object.keys(responses).length} questions)</span>
              <span className="text-muted-foreground text-xs">{showAnswers ? 'Hide' : 'Show'}</span>
            </CardTitle>
          </CardHeader>
          {showAnswers && (
            <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
              {(questionOrder.length > 0 ? questionOrder : Object.keys(responses)).map((qid, idx) => {
                const resp = responses[qid];
                if (!resp) return null;
                const isHighRisk = (resp.score || 0) >= 3;
                return (
                  <div key={qid} className={`p-3 rounded-xl border ${isHighRisk ? 'border-red-200 bg-red-50/50' : 'border-slate-100 bg-muted/20'}`} data-testid={`answer-${qid}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Q{idx + 1} &middot; {resp.domain?.replace(/_/g, ' ')}</p>
                        <p className={`text-sm ${isHighRisk ? 'text-red-800 font-medium' : ''}`}>{resp.question_text || `Question ${qid}`}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${isHighRisk ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {resp.score}/4
                        </span>
                      </div>
                    </div>
                    <p className={`text-xs mt-1.5 ${isHighRisk ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                      Answer: {resp.option_text || `Option ${resp.score}`}
                    </p>
                  </div>
                );
              })}
            </CardContent>
          )}
        </Card>
      )}

      {interpretation && (
        <Card className="border-2 border-primary/20 shadow-sm rounded-2xl bg-primary/5" data-testid="ai-interpretation">
          <CardHeader><CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}><Brain className="h-5 w-5 text-primary" /> AI Clinical Interpretation</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <p className="text-muted-foreground italic">{interpretation.summary}</p>
            <div>
              <h4 className="text-sm font-semibold text-red-600 mb-2">Top 3 Criminogenic Needs</h4>
              <div className="flex flex-wrap gap-2">
                {interpretation.top_3_criminogenic_needs?.map((n, i) => (
                  <Badge key={`item-${i}`} variant="outline" className="border-red-300 text-red-700 bg-red-50">{i + 1}. {n}</Badge>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-emerald-600 mb-2">Identified Strengths</h4>
              <div className="flex flex-wrap gap-2">
                {interpretation.strengths?.map((s, i) => (
                  <Badge key={`item-${i}`} variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">{s}</Badge>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-primary mb-2">Stage of Change Analysis</h4>
              <p className="text-sm text-muted-foreground">{interpretation.stage_of_change_analysis}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { title: '30-Day Plan', items: interpretation.intervention_plan_30_day, color: 'bg-blue-50 border-blue-200' },
                { title: '60-Day Plan', items: interpretation.intervention_plan_60_day, color: 'bg-purple-50 border-purple-200' },
                { title: '90-Day Plan', items: interpretation.intervention_plan_90_day, color: 'bg-indigo-50 border-indigo-200' },
              ].map(plan => (
                <div key={plan.title} className={`rounded-xl p-3 border ${plan.color}`}>
                  <h5 className="text-xs font-semibold text-foreground mb-2">{plan.title}</h5>
                  <ul className="space-y-1">
                    {plan.items?.map((item, i) => (
                      <li key={`item-${i}`} className="text-xs text-muted-foreground flex gap-1">
                        <span className="shrink-0">{i + 1}.</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {interpretation.program_recommendations?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-yellow-600 mb-2">Recommended Programs</h4>
                <div className="flex flex-wrap gap-2">
                  {interpretation.program_recommendations.map((p, i) => (
                    <Badge key={`item-${i}`} className="bg-yellow-100 text-yellow-700 border-yellow-300">{p}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ============================
   STAFF: Strengths Summary Editor
   ============================ */
function StrengthsSummaryPanel({ assessmentId, participantName, headers }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState({ strengths_text: '', areas_to_work_on: '', encouragement: '' });

  const downloadPdf = async (style) => {
    try {
      const res = await fetch(`${API}/api/core8/strengths-summary/${assessmentId}/pdf?style=${style}`, {
        headers: { Authorization: headers.Authorization }
      });
      if (!res.ok) { toast.error('PDF not available'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `strengths_${style}_${participantName.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  useEffect(() => {
    loadSummary();
  }, [assessmentId]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/core8/strengths-summary/${assessmentId}`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
        setEditFields({
          strengths_text: data.strengths_text || '',
          areas_to_work_on: data.areas_to_work_on || '',
          encouragement: data.encouragement || '',
        });
      } else if (res.status === 404) {
        setSummary(null);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const generateSummary = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/core8/strengths-summary/generate`, { method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ assessment_id: assessmentId }),
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
        setEditFields({
          strengths_text: data.strengths_text || '',
          areas_to_work_on: data.areas_to_work_on || '',
          encouragement: data.encouragement || '',
        });
        setEditing(true);
        toast.success('AI summary generated! Review and edit before sending.');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate summary');
      }
    } catch (e) { toast.error('Generation failed'); }
    setGenerating(false);
  };

  const saveSummary = async () => {
    try {
      const res = await fetch(`${API}/api/core8/strengths-summary/${assessmentId}`, { method: 'PUT', headers, credentials: 'include',
        body: JSON.stringify(editFields),
      });
      if (res.ok) {
        toast.success('Summary saved');
        setEditing(false);
        loadSummary();
      }
    } catch (e) { toast.error('Save failed'); }
  };

  const sendToClient = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/api/core8/strengths-summary/${assessmentId}/send`, { method: 'POST', headers, credentials: 'include',
      });
      if (res.ok) {
        toast.success(`Strengths summary sent to ${participantName}`);
        loadSummary();
      }
    } catch (e) { toast.error('Send failed'); }
    setSending(false);
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardContent className="pt-6 text-center">
          <Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-teal-200 shadow-sm rounded-2xl bg-teal-50/30" data-testid="strengths-summary-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Heart className="h-4 w-4 text-teal-600" /> Strengths-Based Summary for {participantName}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {summary?.sent_to_client ? 'This summary has been sent to the client.' : 'Generate, review, edit, and send a supportive message.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!summary ? (
          <Button
            data-testid="generate-summary-btn"
            onClick={generateSummary}
            disabled={generating}
            className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white"
          >
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {generating ? 'Generating AI Summary...' : 'Generate Strengths Summary'}
          </Button>
        ) : editing ? (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Strengths</label>
              <DictationInput
                data-testid="edit-strengths"
                value={editFields.strengths_text}
                onChange={e => setEditFields(prev => ({ ...prev, strengths_text: e.target.value }))}
                rows={3}
                className="rounded-xl"
              showCleanup={false} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Areas to Build On</label>
              <DictationInput
                data-testid="edit-areas"
                value={editFields.areas_to_work_on}
                onChange={e => setEditFields(prev => ({ ...prev, areas_to_work_on: e.target.value }))}
                rows={3}
                className="rounded-xl"
              showCleanup={false} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Encouragement</label>
              <DictationInput
                data-testid="edit-encouragement"
                value={editFields.encouragement}
                onChange={e => setEditFields(prev => ({ ...prev, encouragement: e.target.value }))}
                rows={2}
                className="rounded-xl"
              showCleanup={false} />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveSummary} className="rounded-xl flex-1" data-testid="save-summary-btn">
                <CheckCircle2 className="mr-2 h-4 w-4" /> Save Changes
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)} className="rounded-xl">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-4 border border-teal-100">
              <h4 className="text-sm font-semibold text-emerald-700 mb-1">Strengths</h4>
              <p className="text-sm text-foreground">{summary.strengths_text}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-amber-100">
              <h4 className="text-sm font-semibold text-amber-700 mb-1">Areas to Build On</h4>
              <p className="text-sm text-foreground">{summary.areas_to_work_on}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-blue-100">
              <h4 className="text-sm font-semibold text-blue-700 mb-1">Encouragement</h4>
              <p className="text-sm text-foreground">{summary.encouragement}</p>
            </div>
            {summary.sent_to_client && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Sent to client {summary.sent_at ? `on ${new Date(summary.sent_at).toLocaleDateString()}` : ''}
              </Badge>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setEditing(true)} className="rounded-xl" data-testid="edit-summary-btn">
                <Edit3 className="mr-2 h-4 w-4" /> Edit
              </Button>
              <Button onClick={generateSummary} variant="outline" disabled={generating} className="rounded-xl" data-testid="regenerate-summary-btn">
                <RefreshCw className={`mr-2 h-4 w-4 ${generating ? 'animate-spin' : ''}`} /> Regenerate
              </Button>
              {!summary.sent_to_client && (
                <Button onClick={sendToClient} disabled={sending} className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white" data-testid="send-summary-btn">
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Send to Client
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-teal-100">
              <p className="text-xs text-muted-foreground w-full mb-1">Download as PDF:</p>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full text-xs"
                data-testid="download-card-pdf"
                onClick={() => downloadPdf('card')}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Strengths Card
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full text-xs"
                data-testid="download-letter-pdf"
                onClick={() => downloadPdf('letter')}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Personal Letter
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============================
   STAFF: Completed Dashboard
   ============================ */
function StaffCompletedDashboard({ headers, onViewResults, onAuthorize }) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/core8/completed-dashboard`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAssessments(data.assessments || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleAuthorize = async (participantId, name) => {
    try {
      const res = await fetch(`${API}/api/core8/authorize-reassessment/${participantId}`, { method: 'POST', headers, credentials: 'include',
      });
      if (res.ok) {
        toast.success(`Reassessment authorized for ${name}`);
        loadDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Authorization failed');
      }
    } catch (e) { toast.error('Authorization failed'); }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground mt-3">Loading completed assessments...</p>
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardContent className="pt-8 pb-8 text-center">
          <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">No completed CORE-8 assessments yet.</p>
        </CardContent>
      </Card>
    );
  }

  const levelColors = {
    low: 'bg-emerald-100 text-emerald-700',
    moderate: 'bg-yellow-100 text-yellow-700',
    elevated: 'bg-orange-100 text-orange-700',
    high: 'bg-red-100 text-red-700',
    critical: 'bg-red-200 text-red-800',
  };

  return (
    <div className="space-y-3" data-testid="completed-dashboard">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{assessments.length} completed assessment{assessments.length !== 1 ? 's' : ''}</p>
      </div>
      {assessments.map(a => (
        <Card
          key={a.assessment_id}
          className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-all"
          data-testid={`assessment-card-${a.assessment_id}`}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-foreground font-semibold">{a.participant_name}</p>
                  <Badge className={`text-xs ${levelColors[a.overall_level] || 'bg-muted text-muted-foreground'}`}>
                    {a.overall_level?.toUpperCase()}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Completed {a.completed_at ? new Date(a.completed_at).toLocaleDateString() : 'N/A'}
                  {a.administered_by ? ` by ${a.administered_by}` : ''}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {Object.entries(a.risk_scores || {}).map(([k, v]) => (
                    <span key={k} className={`px-2 py-0.5 rounded-full text-xs ${
                      v.score <= 3 ? 'bg-emerald-50 text-emerald-600' : v.score <= 5 ? 'bg-yellow-50 text-yellow-600' : v.score <= 7 ? 'bg-orange-50 text-orange-600' : 'bg-red-50 text-red-600'
                    }`}>
                      {v.level ? `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v.score}` : k}
                    </span>
                  ))}
                  {a.stage_of_change && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">{a.stage_of_change}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {a.strengths_summary?.sent ? (
                  <Badge className="bg-emerald-50 text-emerald-700 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" /> Summary Sent</Badge>
                ) : a.strengths_summary?.exists ? (
                  <Badge className="bg-amber-50 text-amber-700 text-xs"><Edit3 className="h-3 w-3 mr-1" /> Draft Ready</Badge>
                ) : null}
                <Button
                  size="sm"
                  onClick={() => onViewResults(a.assessment_id, a.participant_id, a.participant_name)}
                  className="rounded-full"
                  data-testid={`view-results-${a.assessment_id}`}
                >
                  <Eye className="h-4 w-4 mr-1" /> View & Manage
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAuthorize(a.participant_id, a.participant_name)}
                  className="rounded-full"
                  data-testid={`authorize-reassess-${a.participant_id}`}
                >
                  <Unlock className="h-4 w-4 mr-1" /> Authorize Retake
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ============================
   CLIENT: My Summary View
   ============================ */
function ClientCompletedView({ headers }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gateInfo, setGateInfo] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/core8/my-summary`, { headers, credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/core8/gate-check`, { headers, credentials: 'include' }).then(r => r.ok ? r.json() : null),
    ]).then(([summaryData, gateData]) => {
      setSummary(summaryData);
      setGateInfo(gateData);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const downloadPdf = async (style) => {
    const aid = summary?.assessment_id || gateInfo?.latest_assessment_id;
    if (!aid) { toast.error('No assessment found'); return; }
    try {
      const res = await fetch(`${API}/api/core8/strengths-summary/${aid}/pdf?style=${style}`, { headers, credentials: 'include' });
      if (!res.ok) { toast.error('PDF not available yet'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my_strengths_${style}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="client-completed-view">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
          CORE-8 Assessment Complete
        </h1>
        <p className="text-muted-foreground mt-2">
          Your support team is reviewing your results and will share feedback with you.
        </p>
      </div>

      {summary?.has_summary ? (
        <Card className="border-2 border-teal-200 shadow-sm rounded-2xl bg-teal-50/20" data-testid="client-strengths-summary">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
              <Heart className="h-5 w-5 text-teal-600" /> A Message from Your Support Team
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white rounded-xl p-4 border border-emerald-100">
              <h4 className="text-sm font-semibold text-emerald-700 mb-2">Your Strengths</h4>
              <p className="text-sm text-foreground leading-relaxed">{summary.strengths_text}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-amber-100">
              <h4 className="text-sm font-semibold text-amber-700 mb-2">Areas to Build On</h4>
              <p className="text-sm text-foreground leading-relaxed">{summary.areas_to_work_on}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-blue-100">
              <h4 className="text-sm font-semibold text-blue-700 mb-2">From Your Team</h4>
              <p className="text-sm text-foreground leading-relaxed">{summary.encouragement}</p>
            </div>
            <div className="flex flex-wrap gap-2 pt-3 border-t border-teal-100">
              <p className="text-xs text-muted-foreground w-full mb-1">Save a copy to keep with you:</p>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full text-xs"
                data-testid="client-download-card"
                onClick={() => downloadPdf('card')}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Pocket Card
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full text-xs"
                data-testid="client-download-letter"
                onClick={() => downloadPdf('letter')}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Full Letter
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm rounded-2xl bg-muted/30">
          <CardContent className="pt-6 pb-6 text-center">
            <Sparkles className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground text-sm">
              Your support team hasn't shared your personalized summary yet. Check back soon!
            </p>
          </CardContent>
        </Card>
      )}

      {gateInfo?.reassessment_authorized && (
        <Card className="border-0 shadow-sm rounded-2xl border-primary/20 bg-primary/5">
          <CardContent className="pt-5 pb-5 text-center space-y-3">
            <p className="text-sm text-foreground font-medium">Your support team has authorized a new assessment.</p>
            <Button
              data-testid="retake-assessment-btn"
              onClick={() => window.location.reload()}
              className="rounded-xl"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Take New Assessment
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


/* ============================
   MAIN COMPONENT
   ============================ */
export default function Core8AssessmentPage() {
  const { user, token } = useAuth();
  const [mode, setMode] = useState('loading');
  const [staffTab, setStaffTab] = useState('assess');
  const [participants, setParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState(null);
  const [interpretation, setInterpretation] = useState(null);
  const [interpreting, setInterpreting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  const isStaff = ['peer_specialist', 'coordinator', 'admin', 'network_admin'].includes(user?.role);
  const isAdmin = ['coordinator', 'admin'].includes(user?.role);
  const headers = { 'Content-Type': 'application/json' };

  useEffect(() => {
    if (isStaff) {
      loadParticipants();
      setMode('select');
    } else {
      // Client: check gate
      checkClientGate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkClientGate = async () => {
    try {
      const res = await fetch(`${API}/api/core8/gate-check`, { headers, credentials: 'include' });
      const data = await res.json();
      if (data.completed && !data.reassessment_authorized) {
        setMode('client_completed');
      } else {
        setMode('select');
      }
    } catch (e) {
      setMode('select');
    }
  };

  const loadParticipants = async () => {
    try {
      const res = await fetch(`${API}/api/users?role=client`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setParticipants(data.users || data || []);
      }
    } catch (e) { console.error(e); }
  };

  const startAssessment = async (participantId) => {
    setLoading(true);
    setError('');
    try {
      const body = participantId ? { participant_id: participantId } : {};
      const res = await fetch(`${API}/api/core8/start`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start');
      setAssessmentId(data.assessment_id);
      setQuestions([data.current_question]);
      setCurrentIndex(0);
      setMode('assess');
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleAnswer = async (questionId, selectedKey) => {
    try {
      const res = await fetch(`${API}/api/core8/${assessmentId}/respond`, { method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ question_id: questionId, selected_key: selectedKey }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');

      if (data.status === 'completed') {
        const rRes = await fetch(`${API}/api/core8/${assessmentId}/results`, { headers, credentials: 'include' });
        const rData = await rRes.json();
        setResults(rData);
        if (isStaff) {
          setMode('results');
        } else {
          // Client just completed — show completed view
          setMode('client_completed');
        }
      } else {
        if (data.next_question) {
          setQuestions(prev => [...prev, data.next_question]);
        }
        setCurrentIndex(prev => prev + 1);
      }
    } catch (e) { setError(e.message); }
  };

  const generateInterpretation = async () => {
    setInterpreting(true);
    try {
      const res = await fetch(`${API}/api/core8/${assessmentId}/interpret`, { method: 'POST', headers, credentials: 'include' });
      const data = await res.json();
      if (res.ok) setInterpretation(data.interpretation);
    } catch (e) { console.error(e); }
    setInterpreting(false);
  };

  const loadHistory = async (participantId) => {
    try {
      const res = await fetch(`${API}/api/core8/participant/${participantId}`, { headers, credentials: 'include' });
      const data = await res.json();
      setHistory(data.assessments || []);
    } catch (e) { console.error(e); }
  };

  const viewPastResults = async (aid, pid, name) => {
    setAssessmentId(aid);
    if (pid) setSelectedParticipant({ id: pid, full_name: name });
    try {
      const rRes = await fetch(`${API}/api/core8/${aid}/results`, { headers, credentials: 'include' });
      const rData = await rRes.json();
      setResults(rData);
      setInterpretation(rData.ai_interpretation || null);
      setMode('results');
    } catch (e) { setError(e.message); }
  };

  // Loading state
  if (mode === 'loading') {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Client completed view
  if (mode === 'client_completed' && !isStaff) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <ClientCompletedView headers={headers} />
      </div>
    );
  }

  // Client self-assessment start
  if (!isStaff && mode === 'select') {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>CORE-8 Profile Assessment</h1>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            32 questions about where you are right now. No right or wrong answers — just be real.
          </p>
        </div>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
              <p>Takes about 10-15 minutes. Your responses help your support team understand how best to help you.</p>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button
              data-testid="start-assessment-btn"
              onClick={() => startAssessment(null)}
              disabled={loading}
              className="w-full py-3 rounded-xl"
              size="lg"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
              Begin Assessment
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Staff: main view with tabs
  if (isStaff && mode === 'select') {
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>CORE-8 Profile Assessment</h1>
            <p className="text-muted-foreground text-sm">Administer, review, and manage client assessments</p>
          </div>
        </div>

        {/* Staff Tabs */}
        <div className="flex gap-1 bg-muted rounded-xl p-1" data-testid="staff-tabs">
          {[
            { key: 'assess', label: 'Administer Assessment', icon: Shield },
            ...(isAdmin ? [{ key: 'dashboard', label: 'Completed Assessments', icon: ClipboardCheck }] : []),
          ].map(tab => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => setStaffTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                staffTab === tab.key ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}

        {/* New Assessment Tab */}
        {staffTab === 'assess' && (
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}><Users className="h-4 w-4" /> Select Participant</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {participants.map(p => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between p-3 rounded-xl bg-muted/50 border border-border hover:border-primary/40 transition-all"
                  >
                    <div>
                      <p className="text-foreground font-medium">{p.full_name}</p>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        onClick={() => { setSelectedParticipant(p); loadHistory(p.id); setMode('history'); }}
                      >
                        <FileText className="h-4 w-4 mr-1" /> History
                      </Button>
                      <Button
                        data-testid={`start-for-${p.id}`}
                        size="sm"
                        onClick={() => { setSelectedParticipant(p); startAssessment(p.id); }}
                        disabled={loading}
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Administer'}
                      </Button>
                    </div>
                  </div>
                ))}
                {participants.length === 0 && (
                  <p className="text-muted-foreground text-center py-8">No participants found</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed Dashboard Tab */}
        {staffTab === 'dashboard' && isAdmin && (
          <StaffCompletedDashboard
            headers={headers}
            onViewResults={(aid, pid, name) => viewPastResults(aid, pid, name)}
            onAuthorize={() => {}}
          />
        )}
      </div>
    );
  }

  // History view
  if (mode === 'history') {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        <Button variant="ghost" className="text-muted-foreground" onClick={() => setMode('select')}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Assessment History: {selectedParticipant?.full_name}</h2>
        {history.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="pt-6 text-center text-muted-foreground">
              No assessments yet for this participant.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {history.map(a => (
              <Card key={a.id} className="border-0 shadow-sm rounded-2xl hover:shadow-md cursor-pointer transition-all" onClick={() => viewPastResults(a.id, selectedParticipant?.id, selectedParticipant?.full_name)}>
                <CardContent className="pt-4 pb-4 flex items-center justify-between">
                  <div>
                    <p className="text-foreground font-medium">{a.status === 'completed' ? 'Completed' : 'In Progress'}</p>
                    <p className="text-xs text-muted-foreground">{new Date(a.started_at).toLocaleDateString()} {a.administered_by_name && `by ${a.administered_by_name}`}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {a.overall_risk && <RiskLevelBadge level={a.overall_risk.level} score={a.overall_risk.highest_score} />}
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Assessment in progress
  if (mode === 'assess') {
    const currentQ = questions[currentIndex];
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" className="text-muted-foreground" onClick={() => { if (window.confirm('Leave assessment? Progress will be saved.')) setMode('select'); }}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Exit
          </Button>
          {selectedParticipant && <span className="text-sm text-muted-foreground">{selectedParticipant.full_name}</span>}
        </div>

        <Progress value={((currentIndex) / 32) * 100} className="h-2" data-testid="assessment-progress" />

        {currentQ ? (
          <QuestionView
            question={currentQ}
            onAnswer={handleAnswer}
            index={currentIndex}
            total={32}
          />
        ) : (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground mt-3">Loading question...</p>
          </div>
        )}
      </div>
    );
  }

  // Results (staff)
  if (mode === 'results') {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        <Button variant="ghost" className="text-muted-foreground" onClick={() => { setMode('select'); setResults(null); setInterpretation(null); }}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <StaffResultsView
          results={results}
          onInterpret={generateInterpretation}
          interpreting={interpreting}
          interpretation={interpretation}
          participantName={results?.participant_name || selectedParticipant?.full_name}
        />

        {/* Strengths Summary Panel for admin/coordinator */}
        {isAdmin && assessmentId && (
          <StrengthsSummaryPanel
            assessmentId={assessmentId}
            participantName={results?.participant_name || selectedParticipant?.full_name || 'Client'}
            headers={headers}
          />
        )}
      </div>
    );
  }

  return null;
}
